import {
  HomeOutlined,
  FolderOpenOutlined,
  BranchesOutlined,
  KeyOutlined,
  UnorderedListOutlined,
  ToolOutlined,
  ControlOutlined,
  DashboardOutlined,
  UserOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useParams } from '@tanstack/react-router';
import * as routeConst from '_/common/routes/routes.constants';
import { IProjectRouteParams } from '_/common/routes/routes.types';
import { SidebarItem } from '_/components/common/layout/AppSidebar';
import { useAuth } from '_/contexts/AuthContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { getProjectUrl } from '_/features/projects/common/utils/projectUrl.utils';
import React, { useMemo } from 'react';

// Create icon elements once to prevent re-renders
const homeIcon = <HomeOutlined />;
const folderOpenIcon = <FolderOpenOutlined />;
const branchesIcon = <BranchesOutlined />;
const keyIcon = <KeyOutlined />;
const unorderedListIcon = <UnorderedListOutlined />;
const toolIcon = <ToolOutlined />;
const controlIcon = <ControlOutlined />;
const dashboardIcon = <DashboardOutlined />;
const userIcon = <UserOutlined />;
const userAddIcon = <UserAddOutlined />;

export function useProjectSidebarItems(): SidebarItem[] {
  const params = useParams({ strict: false }) as Partial<IProjectRouteParams>;
  const projectSlug = params.projectSlug || '';
  const { selectedOrgSlug } = useOrganization();
  const { isSuperAdmin } = useAuth();
  const projectUrl = getProjectUrl({ orgSlug: selectedOrgSlug || '', projectSlug: projectSlug });

  return useMemo(() => {
    const items: SidebarItem[] = [
      {
        icon: homeIcon,
        text: 'Project Home',
        href: `${projectUrl}/${routeConst.HOME_PAGE_ID}`,
      },
      {
        icon: folderOpenIcon,
        text: 'Collections',
        href: `${projectUrl}/collections`,
      },
      {
        icon: branchesIcon,
        text: 'Flows',
        href: `${projectUrl}/${routeConst.FLOWS_PAGE_ID}`,
      },
      {
        icon: keyIcon,
        text: 'Login Pools',
        href: `${projectUrl}/loginpools`,
      },
      {
        icon: unorderedListIcon,
        text: 'QA Queue',
        href: `${projectUrl}/qaqueue`,
      },
      {
        icon: toolIcon,
        text: 'Fix Queue',
        href: `${projectUrl}/fixqueue`,
      },
      {
        icon: dashboardIcon,
        text: 'Cell Metrics',
        href: `${projectUrl}/${routeConst.CELL_METRICS_PAGE_ID}`,
      },
    ];

    if (isSuperAdmin) {
      items.push({
        icon: controlIcon,
        text: 'Cell Routes',
        href: `${projectUrl}/${routeConst.CELL_ROUTES_PAGE_ID}`,
      });
    }

    items.push(
      {
        icon: userIcon,
        text: 'My QA',
        href: `${projectUrl}/myqa`,
      },
      {
        icon: userAddIcon,
        text: 'My Fixing',
        href: `${projectUrl}/myfixing`,
      },
    );

    return items;
  }, [projectUrl, isSuperAdmin]);
}
